import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";

import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PercentIcon from "@mui/icons-material/Percent";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLoanById } from "../../api/api";
import { useNavigate } from "react-router-dom";
import { handleLeadRedirect } from "../../utils/leadRedirect";

// ================= FORMAT =================
const formatCurrency = (val: any) =>
  val ? `₹ ${Number(val).toLocaleString("en-IN")}` : "-";

const formatDate = (date: any) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ================= STYLES =================
const cardStyle = {
  borderRadius: "16px",
  background: "#ffffff",
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  border: "1px solid #f1f5f9",
};

const primaryButton = {
  borderRadius: "10px",
  textTransform: "none",
  px: 2.5,
  fontWeight: 500,
  boxShadow: "0 2px 6px rgba(79,70,229,0.2)",
  background: "linear-gradient(135deg, #6366f1, #4f46e5)",
};

const outlinedButton = {
  borderRadius: "10px",
  textTransform: "none",
  px: 2.5,
  borderColor: "#e2e8f0",
  color: "#334155",
};

// ================= FIELD =================
const Field = ({ label, value }: any) => (
  <Box>
    <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 0.3 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#0f172a" }}>
      {value || "-"}
    </Typography>
  </Box>
);

// ================= COMPONENT =================
const LoanView = () => {
  const { id } = useParams();
  const [loan, setLoan] = useState<any>(null);
  const navigate = useNavigate();
  useEffect(() => {
    fetchLoan();
  }, []);

  const fetchLoan = async () => {
    try {
      const res = await getLoanById(id as string);
      const data = res.data?.data;

      const lead = data?.leadId;

      // 🔒 FLOW GUARD
      if (lead?.status !== "approved" && lead?.status !== "disbursed") {
        handleLeadRedirect(lead, navigate);
        return;
      }

      setLoan(data);
    } catch (err) {
      console.log(err);
    }
  };

  if (!loan) {
    return <Typography sx={{ p: 2 }}>Loading...</Typography>;
  }

  const lead = loan?.leadId || {};
  const status = lead?.status || "pending";

  const steps = ["pending", "credit", "approved", "disbursed"];
  const activeStep = steps.indexOf(status);

  // ================= SUMMARY CARD =================
  const summaryCard = (icon: any, label: string, value: any) => (
    <Paper sx={{ ...cardStyle, p: 2, display: "flex", gap: 2 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          background: "#eef2ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4f46e5",
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>{label}</Typography>
        <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
          Loan Overview
        </Typography>
      </Box>

      {/* SUMMARY */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 2,
          mb: 3,
        }}
      >
        {summaryCard(
          <AccountBalanceIcon />,
          "Loan Amount",
          formatCurrency(loan.loanAmount),
        )}
        {summaryCard(<PercentIcon />, "ROI", loan.roi || "-")}
        {summaryCard(
          <AccessTimeIcon />,
          "Tenure",
          loan.tenure ? `${loan.tenure} months` : "-",
        )}
        {summaryCard(
          <CalendarMonthIcon />,
          "EMI Date",
          formatDate(loan.dueDate),
        )}
      </Box>

      {/* STEPPER */}
      <Paper sx={{ ...cardStyle, p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>
                <Typography sx={{ textTransform: "capitalize" }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* MAIN */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { md: "2fr 1fr", xs: "1fr" },
          gap: 2,
        }}
      >
        {/* LEFT */}
        <Paper sx={{ ...cardStyle, p: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>Customer Info</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Field label="Name" value={lead.customerName} />
            <Field label="Lead ID" value={lead.leadCode} />
            <Field label="Mobile" value={lead.mobile} />
            <Field label="Product" value={lead.productType} />
            <Field label="Pending Reason" value={lead.pendingReason || "-"} />
            <Field label="Broker Name" value={lead?.bankDetails?.brokerName} />

            <Field
              label="Broker Mobile"
              value={lead?.bankDetails?.brokerMobile}
            />
          </Box>

          <Box sx={{ my: 2, borderBottom: "1px solid #e5e7eb" }} />

          <Typography sx={{ fontWeight: 600, mb: 2 }}>Bank Details</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Field label="Bank" value={lead?.bankDetails?.bankName} />
            <Field label="Branch" value={lead?.bankDetails?.branch} />
            <Field label="Scheme" value={loan.schemeType} />
            <Field label="Created By" value={loan?.createdBy?.name} />
          </Box>
        </Paper>

        {/* RIGHT */}
        <Paper sx={{ ...cardStyle, p: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 2 }}>
            Charges Summary
          </Typography>

          <Field
            label="Gap Interest"
            value={formatCurrency(loan.gapInterest)}
          />
          <Field
            label="File Charges"
            value={formatCurrency(loan.fileCharges)}
          />
          <Field label="Net Amount" value={formatCurrency(loan.netAmount)} />
          <Field label="Created Date" value={formatDate(loan.createdAt)} />
        </Paper>
      </Box>
    </Box>
  );
};

export default LoanView;
